/**
 * reportBuilder.ts — the report builder's rules, pure (Phase 2 / 2.5,
 * §16.7, §20 B-1..B-11, §20.6). The UI's mirror of the server's closed
 * token table: which objects, dimensions, measures, filters and date fields
 * exist, with their labels. The SERVER is the authority — an unknown slug is
 * its named 400 — this file only decides what the controls offer and how a
 * result reads.
 *
 *   B-4  countedRecords + excludedNonGbp sit beside every MONEY measure; the
 *        count measure shows none (its keys arrive null, and null renders as
 *        no column, never as 0).
 *   B-5  group values render VERBATIM: a known slug gets its label, anything
 *        else its own text, so "Outbound" and "outbound" stay two rows; the
 *        null group reads "(none)".
 *   B-11 five measures. "Σ fenced value" is the deal's OWN value (/reports'
 *        arithmetic) and stays the default money measure; "Σ forecast
 *        amount" is the effective amount (/forecast's) — two questions, both
 *        answerable, labelled as such.
 *   Q1   the truncation line is rendered ONLY when N > 200, verbatim.
 */
import type {
  SalesReportDefinition, SalesReportGroup, SalesReportMeasure, SalesReportObject, SalesReportResult,
} from '../../../../api/types'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import { money, nonGbpNote } from '../../../../lib/money'
import { SLUG_TO_STAGE } from '../../api'
import { sourceLabel, forecastCategoryLabel } from './vocab'

export const REPORT_CAP = 200

export interface Option { value: string; label: string }

export const OBJECT_OPTIONS: ReadonlyArray<{ value: SalesReportObject; label: string }> = [
  { value: 'deals', label: 'Deals' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'activities', label: 'Activities' },
]

/** Dimensions per object, in the server's order (its 400 sentence lists them so). */
export const DIMENSIONS: Record<SalesReportObject, ReadonlyArray<Option>> = {
  deals: [
    { value: 'stage', label: 'Stage' }, { value: 'owner', label: 'Owner' }, { value: 'account', label: 'Account' },
    { value: 'source', label: 'Source' }, { value: 'priority', label: 'Priority' },
    { value: 'forecast_category', label: 'Forecast category (effective)' },
    { value: 'month_created', label: 'Month created' }, { value: 'month_closed', label: 'Month closed' },
    { value: 'month_expected_close', label: 'Month of expected close' },
  ],
  contacts: [
    { value: 'status', label: 'Status' }, { value: 'owner', label: 'Owner' }, { value: 'account', label: 'Account' },
    { value: 'source', label: 'Source' }, { value: 'category', label: 'Category' },
    { value: 'prospect_segment', label: 'Segment' }, { value: 'location', label: 'Location' },
    { value: 'month_created', label: 'Month created' },
  ],
  activities: [
    { value: 'type', label: 'Type' }, { value: 'status', label: 'Status' }, { value: 'owner', label: 'Owner' },
    { value: 'month_due', label: 'Month due' }, { value: 'month_created', label: 'Month created' },
    { value: 'month_completed', label: 'Month completed' },
  ],
}

/** Measures per object. Deals carry the four money measures; the other two
 *  objects count only (the server refuses more by name). */
export const MEASURES: Record<SalesReportObject, ReadonlyArray<{ value: SalesReportMeasure; label: string; money: boolean }>> = {
  deals: [
    { value: 'count', label: 'Count', money: false },
    { value: 'sum_fenced', label: 'Σ fenced value (the deals’ own value)', money: true },
    { value: 'sum_forecast', label: 'Σ forecast amount (override amount where set)', money: true },
    { value: 'sum_weighted', label: 'Σ weighted (effective)', money: true },
    { value: 'avg_fenced', label: 'Average fenced value', money: true },
  ],
  contacts: [{ value: 'count', label: 'Count', money: false }],
  activities: [{ value: 'count', label: 'Count', money: false }],
}

/** Date fields a range may bind to, per object. */
export const RANGE_FIELDS: Record<SalesReportObject, ReadonlyArray<Option>> = {
  deals: [{ value: 'created_at', label: 'Created' }, { value: 'closed_at', label: 'Closed' }, { value: 'expected_close', label: 'Expected close' }],
  contacts: [{ value: 'created_at', label: 'Created' }],
  activities: [{ value: 'due_at', label: 'Due' }, { value: 'created_at', label: 'Created' }, { value: 'completed_at', label: 'Completed' }],
}

// ── §35: the id-valued filters ──────────────────────────────────────────────

/** The four id-valued filter slugs — the wire's names (reportquery.go
 *  filterSlugs). Their values are bare ids; a PICKER supplies them. */
export type IdFilterKey = 'assignedEmployeeId' | 'accountId' | 'contactId' | 'dealId'
/** The three the builder resolves through a by-id read on view load (the
 *  owner resolves through the directory the OwnerSelect already holds). */
export type ResolvableIdFilter = Exclude<IdFilterKey, 'assignedEmployeeId'>

/** Which id filters each object accepts — the server's allowlist verbatim
 *  (deals :136, contacts :160, activities :176), owner first. A key offered
 *  for the wrong object would be the server's named 400. */
export const ID_FILTERS: Record<SalesReportObject, ReadonlyArray<IdFilterKey>> = {
  deals: ['assignedEmployeeId', 'accountId'],
  contacts: ['assignedEmployeeId', 'accountId'],
  activities: ['assignedEmployeeId', 'contactId', 'dealId'],
}

/** Beside a picker whose id came from a saved view: while the by-id read
 *  is in flight. */
export const ID_FILTER_RESOLVING = 'Resolving…'

/** Ruling 3 (§35, 2026-09-20): a deleted target renders the RAW id in the
 *  control plus a plain client-side note — per object, so a support reply
 *  can say which one fired (the §35.5 census: "no longer exists" is already
 *  eight sentences; these name the object and say "available"). Nothing is
 *  fabricated: the id stays the value, the note is the client's own. */
export const ID_FILTER_GONE: Record<ResolvableIdFilter, string> = {
  accountId: 'This account is no longer available.',
  contactId: 'This contact is no longer available.',
  dealId: 'This deal is no longer available.',
}

/** Any other failure of the by-id read: the helper's message when the server
 *  named one, else this fallback — the id still stands in the control. */
export const ID_FILTER_UNRESOLVED: Record<ResolvableIdFilter, string> = {
  accountId: 'This account could not be resolved.',
  contactId: 'This contact could not be resolved.',
  dealId: 'This deal could not be resolved.',
}

/** The note for a by-id read that failed: a 404 is the deleted-target
 *  sentence (the server's body is the bare `not found`, which the shared
 *  helper swaps for a fallback by design — so the status, not the text, is
 *  what this reads); anything else goes through the shared helper with the
 *  per-object fallback. The helper itself is untouched (ruling 3). */
export function idFilterFailureNote(key: ResolvableIdFilter, e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return ID_FILTER_GONE[key]
  return apiErrorMessage(e, ID_FILTER_UNRESOLVED[key])
}

/** The starting question: deals by stage, counted, as a bar. */
export function defaultDefinition(): SalesReportDefinition {
  return { v: 1, object: 'deals', filters: {}, groupBy: 'stage', measure: 'count', chart: 'bar' }
}

/** Switching object resets group-by and measure to that object's first
 *  entries and drops filters/range that belong to the old object — a
 *  definition is always one the server will accept. */
export function withObject(def: SalesReportDefinition, object: SalesReportObject): SalesReportDefinition {
  if (object === def.object) return def
  return { v: 1, object, filters: {}, groupBy: DIMENSIONS[object][0].value, measure: 'count', chart: def.chart }
}

export function isMoneyMeasure(measure: SalesReportMeasure): boolean {
  return measure !== 'count'
}

export function measureLabel(measure: SalesReportMeasure): string {
  return MEASURES.deals.find(m => m.value === measure)?.label ?? measure
}

export function dimensionLabel(object: SalesReportObject, groupBy: string): string {
  return DIMENSIONS[object].find(d => d.value === groupBy)?.label ?? groupBy
}

/** What a null group reads as: a REAL group, in words. */
export const NONE_GROUP_LABEL = '(none)'

/** A group value, VERBATIM: known slugs of the closed vocabularies get their
 *  label (stage, forecast category, source, priority), everything else is
 *  the stored text exactly — so "Outbound" and "outbound" are two rows and
 *  nothing is folded. The month dimensions read as their YYYY-MM key. */
export function groupLabel(groupBy: string, value: string | null): string {
  if (value === null) return NONE_GROUP_LABEL
  switch (groupBy) {
    case 'stage': return SLUG_TO_STAGE[value] ?? value
    case 'forecast_category': return forecastCategoryLabel(value)
    case 'source': return sourceLabel(value)
    case 'priority': return ({ low: 'Low', medium: 'Medium', high: 'High' } as Record<string, string>)[value] ?? value
    default: return value
  }
}

/** The truncation line, §20.6 Q1 VERBATIM — rendered ONLY when N > 200. N is
 *  the server's exact groupCount, never a bounded guess. */
export function truncationLine(res: SalesReportResult): string | null {
  if (res.groupCount <= REPORT_CAP) return null
  return `Showing ${res.groups.length} of ${res.groupCount} groups — the largest ${res.groups.length} by ${measureLabel(res.definition.measure)}. `
    + `The totals above cover all ${res.groupCount}. Narrow the range or choose a coarser group-by to see every group; the CSV export carries all ${res.groupCount}.`
}

/** A measure cell: pence as money, or "—" when the server sent null (an
 *  average over nothing, or the count measure's money keys). */
export function measureCell(pence: number | null): string {
  return pence === null ? '—' : money(pence / 100)
}

/** "N of M counted" beside a money measure — the FENCED count, never the
 *  bare row count (B-4); and the fence's exclusion note in the house idiom. */
export function countedCell(g: Pick<SalesReportGroup, 'count' | 'countedRecords'>): string {
  return g.countedRecords === null ? String(g.count) : `${g.countedRecords} of ${g.count}`
}

export function excludedNote(excluded: number | null): string {
  return excluded === null ? '' : nonGbpNote(excluded, 'deal')
}

/** The bars: label + the measure (or count) per group, in wire order. */
export function chartRows(res: SalesReportResult): Array<{ label: string; value: number }> {
  const isMoney = isMoneyMeasure(res.definition.measure)
  return res.groups.map(g => ({
    label: groupLabel(res.definition.groupBy, g.value),
    value: isMoney ? (g.measurePence ?? 0) / 100 : g.count,
  }))
}

/** What the Export button tells the user BEFORE they click (§20.6 Q2b): the
 *  export is complete and it is recorded — who, what, when — for owners and
 *  admins to see. */
export const EXPORT_NOTE = 'The CSV carries every group, uncapped. Every export is recorded — who asked, what was asked and when — and owners and admins can see the record.'

/** The toast after a download, from the response's own headers. */
export function exportedSentence(rows: number, excluded: number): string {
  const base = `Exported ${rows} ${rows === 1 ? 'group' : 'groups'} — this export is recorded.`
  return excluded > 0 ? `${base} ${nonGbpNote(excluded, 'deal')}.` : base
}

/** The saved-view body for a definition: object 'sales.reports', the whole
 *  definition as the opaque filters (versioned by its own v). */
export function savedViewBody(name: string, def: SalesReportDefinition) {
  return { object: 'sales.reports', name, filters: def as unknown as Record<string, unknown> }
}

/** Read a saved view back into a definition; null when it is not one this
 *  build understands (v ≠ 1) — never guessed. */
export function definitionFromSaved(filters: Record<string, unknown>): SalesReportDefinition | null {
  if (filters['v'] !== 1 || typeof filters['object'] !== 'string' || typeof filters['groupBy'] !== 'string') return null
  return filters as unknown as SalesReportDefinition
}
